import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import CONTENT from '../../content/content-ar.json';

/** Configuración de carrito y formato de precios por país. */
export interface RackeoCartConfig {
  currency: number;
  tld: string;
  symbol: string;
  currencyHint: string;
  priceLocale: string;
}

/** Precios demo home (webHosting, vps, dedicated). */
export interface RackeoPricingData {
  currency: string;
  symbol: string;
  plans: {
    webHosting: Record<string, number>;
    vps: Record<string, number>;
    dedicated: Record<string, number>;
  };
}

/** Contrato anual de Web Hosting en JSON (1, 2 o 3 años). */
export interface RackeoPlanContract {
  years: number;
  total: number;
}

/** Contrato con precio anual derivado al cargar (total ÷ years). */
export interface RackeoPlanContractNormalized extends RackeoPlanContract {
  perYear: number;
}

/** Fila de precios en tabla comparativa de Web Hosting (tres planes). */
export interface RackeoComparisonPriceRow {
  label: string;
  plan1: string;
  plan2: string;
  plan3: string;
}

/** Plan Web Hosting Pro o Pyme con contratos por duración. */
export interface RackeoWebHostingPlan {
  id: string;
  pid: number;
  cartPid?: number;
  name: string;
  subtitle: string;
  features: Array<{ label: string; value: string }>;
  contracts: RackeoPlanContract[];
  colorClass: string;
}

/** Web Hosting Pro: planes (la tabla comparativa se deriva de los contratos). */
export interface RackeoWebHostingData {
  plans: RackeoWebHostingPlan[];
}

/** Contacto cabecera (teléfono y soporte). */
export interface RackeoContactData {
  phoneDisplay: string;
  phoneTel: string;
  phoneKicker: string;
  chatLabel: string;
  chatUrl?: string;
}

/** Precios VPS SSD en megamenú (por período de facturación). */
export interface RackeoMenuVpsPriceRow {
  label: string;
  mo: number;
  tri: number;
  sem: number;
  an: number;
  bi: number;
  triAn: number;
}

/** Panel de precios en megamenú (sustituye imágenes promocionales). */
export interface RackeoMenuPromoData {
  domainOfferPrice: number;
  vpsPlans: RackeoMenuVpsPriceRow[];
}

/** Home: hero, banners, CTA. */
export interface RackeoHomeData {
  heroImage: string;
  heroAlt?: string;
  banners: Array<{ image: string; url: string; alt: string }>;
  ctaImage: string;
  ctaUrl: string;
  ctaAlt?: string;
}

/** Web Hosting Pyme: misma estructura que Web Hosting Pro, planes y precios por país. */
export interface RackeoWebHostingPymeData extends RackeoWebHostingData {
  heroImage?: string;
  heroAlt?: string;
}

/** Contrato mensual (1, 3, 6, 12 meses) para planes Reseller. */
export interface RackeoHostingResellerContract {
  months: number;
  monthlyPrice: number;
}

/** Plan Hosting Reseller (40–160 GB disk, WHM/cPanel). */
export interface RackeoHostingResellerPlan {
  id: string;
  pid: number;
  cartPid?: number;
  name: string;
  subtitle: string;
  diskGb: number;
  features: Array<{ label: string; value: string }>;
  contracts: RackeoHostingResellerContract[];
  fromMonthly: number;
  colorClass: string;
}

/** Precios en tabla comparativa (cuatro planes). */
export interface RackeoHostingResellerPriceRow {
  label: string;
  plan1: string;
  plan2: string;
  plan3: string;
  plan4: string;
}

/** Hosting Reseller: datos por país (panel WHM, cart pids). */
export interface RackeoHostingResellerData {
  heroImage?: string;
  heroAlt?: string;
  plans: RackeoHostingResellerPlan[];
  comparisonPrices: RackeoHostingResellerPriceRow[];
}

/** Contrato VPS: precio mostrado como equivalente mensual. */
export interface RackeoVirtualizacionContract {
  label: string;
  monthlyPrice: number;
}

/** Plan VPS (SSD): precios se pueden completar desde menuPromo.vpsPlans en el componente. */
export interface RackeoVirtualizacionPlan {
  id: string;
  pid: number;
  cartPid?: number;
  name: string;
  subtitle: string;
  features: Array<{ label: string; value: string }>;
  contracts?: RackeoVirtualizacionContract[];
  colorClass: string;
}

/** Fila de precios en tabla comparativa (7 columnas, una por plan SSD). */
export interface RackeoVirtualizacionPriceRow {
  label: string;
  values: number[];
}

/** Producto adicional (licencias, packs). */
export interface RackeoVirtualizacionAddOn {
  title: string;
  image: string;
  imageAlt?: string;
  price?: number;
  priceDecimals?: number;
  priceNote?: string;
  description: string;
  benefits: string[];
  paymentHint?: string;
}

/** Virtualización / VPS: hero opcional; planes y tablas se arman con menuPromo + defaults si faltan datos. */
export interface RackeoVpsSpec {
  disk: string;
  transfer: string;
  ram: string;
  cpu: string;
  uptime: string;
}

export interface RackeoVirtualizacionData {
  heroImage?: string;
  heroAlt?: string;
  pids?: number[];
  ramLabels?: string[];
  specs?: RackeoVpsSpec[];
  fallbackMonthlyPrices?: number[];
  plans?: RackeoVirtualizacionPlan[];
  comparisonPriceRows?: RackeoVirtualizacionPriceRow[];
  addOns?: RackeoVirtualizacionAddOn[];
}

/** Plan servidor dedicado (hardware fijo; precio mensual viene del JSON por país). */
export interface RackeoServidorDedicadoPlanSpec {
  id: string;
  pid: number;
  name: string;
  model: string;
  cpuLine: string;
  ramLabel: string;
  diskLabel: string;
  raidLabel: string;
  tarjetaLabel: string;
  traficoLabel: string;
  disponibleLabel: string;
  bullets: string[];
  footerNote: string;
}

type LiteralUnion<T extends U, U = string> = T | (U & Record<never, never>);

export type RackeoSdSupportCell = LiteralUnion<'tick' | 'cross'>;

export interface RackeoSdSupportRow {
  feature: string;
  helpText?: string;
  cells: RackeoSdSupportCell[];
}

/** Bloque texto + icono (Generales / Características). */
export interface RackeoSdTextBlock {
  title: string;
  image?: string;
  imageAlt?: string;
  paragraphs: string[];
}

export interface RackeoServidoresDedicadosData {
  heroImage?: string;
  heroAlt?: string;
  planPrices?: number[];
  planPriceDecimals?: number;
  addonPricing?: Array<{ price: number; priceDecimals?: number }>;
  supportFirstPrices?: number[];
  supportExtraPrices?: number[];
  supportPriceDecimals?: number;
}

export interface RackeoContent {
  cart: RackeoCartConfig;
  home: RackeoHomeData;
  pricing: RackeoPricingData;
  webHosting: RackeoWebHostingData;
  webHostingPyme?: RackeoWebHostingPymeData;
  menuPromo?: RackeoMenuPromoData;
  contact: RackeoContactData;
  virtualizacion?: RackeoVirtualizacionData;
  servidoresDedicados?: RackeoServidoresDedicadosData;
  hostingReseller?: RackeoHostingResellerData;
}

@Injectable({
  providedIn: 'root'
})
export class RackeoLocaleService {
  private readonly content$ = new BehaviorSubject<RackeoContent | null>(null);

  constructor() {
    this.content$.next(CONTENT as RackeoContent);
  }

  getContent(): Observable<RackeoContent | null> {
    return this.content$.asObservable();
  }

  getContentSnapshot(): RackeoContent | null {
    return this.content$.getValue();
  }
}
